import { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { Circle, Diamond, Hand, Check, X } from 'lucide-react';
import { playCorrect, playWrong } from '../../utils/sounds';
import type { PrecisionProps, TrialOutcome, TrialRecord } from './types';
import { classifyReaction, getPrecisionRules, summarizeTrials } from './rules';
import { PrecisionHUD, PrecisionStats, TrialStrip } from './PrecisionUI';

type Phase = 'wait' | 'decoy' | 'go' | 'feedback';
export default function PrecisionReaction({ game, mode, tier, onComplete, onFail }: PrecisionProps) {
  const rules = getPrecisionRules(game, mode, tier);
  const [round, setRound] = useState(0);
  const [phase, setPhase] = useState<Phase>('wait');
  const [trials, setTrials] = useState<TrialRecord[]>([]);
  const [feedback, setFeedback] = useState('');
  const [remaining, setRemaining] = useState(1);
  const phaseRef = useRef<Phase>('wait');
  const trialRef = useRef<TrialRecord[]>([]);
  const startRef = useRef(0);
  const sessionStart = useRef(performance.now());
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const locked = useRef(false);
  const finished = useRef(false);
  const resolveRef = useRef<(outcome: TrialOutcome, time: number | null) => void>(() => {});
  const callbacks = useRef({ onComplete, onFail });
  callbacks.current = { onComplete, onFail };
  const stats = summarizeTrials(trials, rules.rounds);
  const shapeRule = rules.level >= 2 && round % 4 >= 2;
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const schedule = (fn: () => void, ms: number) => timers.current.push(setTimeout(fn, ms));
  const show = (next: Phase) => { phaseRef.current = next; setPhase(next); };

  resolveRef.current = (outcome, time) => {
    if (locked.current || finished.current) return;
    locked.current = true;
    clearTimers();
    const label = { perfect: '精准命中', good: '有效命中', wrong: '误点干扰', timeout: '超过时限', early: '提前抢拍' }[outcome];
    const next = [...trialRef.current, { index: round, outcome, responseMs: time, label }];
    trialRef.current = next;
    setTrials(next);
    setFeedback(label);
    show('feedback');
    if (outcome === 'perfect' || outcome === 'good') playCorrect(); else playWrong();
    const summary = summarizeTrials(next, rules.rounds);
    const failed = summary.mistakes >= rules.mistakeLimit;
    schedule(() => {
      if (failed || next.length >= rules.rounds) {
        if (finished.current) return;
        finished.current = true;
        const precision = { ...summary, version: 1 as const, tier: rules.level, trials: next, durationSeconds: (performance.now() - sessionStart.current) / 1000,
          failureReason: failed ? `本轮失误达到 ${rules.mistakeLimit} 次。先认清信号，准确优先于速度。` : undefined };
        const replay = { mechanic: 'reaction', precision };
        if (failed) callbacks.current.onFail(replay); else callbacks.current.onComplete(summary.score, replay);
      } else setRound(v => v + 1);
    }, 650);
  };

  useEffect(() => {
    finished.current = false;
    locked.current = false;
    show('wait');
    setRemaining(1);
    // Independent jitter and multiple decoys prevent a predictable tap rhythm.
    let delay = rules.waitMin + Math.random() * (rules.waitMax - rules.waitMin);
    const count = Math.random() < 0.8 ? rules.decoyCount : 0;
    for (let i = 0; i < count; i++) {
      const decoyAt = delay;
      schedule(() => show('decoy'), decoyAt);
      schedule(() => show('wait'), decoyAt + 360);
      delay += 750 + Math.random() * 800;
    }
    schedule(() => show('go'), delay);
    return clearTimers;
  }, [round]);

  // Start the response clock after the stimulus is committed to the screen.
  useLayoutEffect(() => {
    if (phase !== 'go') return;
    startRef.current = performance.now();
    const deadline = rules.reactionWindow;
    const timer = setTimeout(() => resolveRef.current('timeout', null), deadline);
    const interval = setInterval(() => setRemaining(Math.max(0, 1 - (performance.now() - startRef.current) / deadline)), 40);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, [phase, rules.reactionWindow]);

  useEffect(() => () => { finished.current = true; clearTimers(); }, []);

  const tap = () => {
    if (locked.current || finished.current || phaseRef.current === 'feedback') return;
    if (phaseRef.current === 'go') {
      const ms = performance.now() - startRef.current;
      resolveRef.current(classifyReaction(ms, rules.perfectWindow, rules.reactionWindow), Math.round(ms));
    } else resolveRef.current(phaseRef.current === 'decoy' ? 'wrong' : 'early', null);
  };
  const isGood = trials.at(-1)?.outcome === 'perfect' || trials.at(-1)?.outcome === 'good';
  const Shape = shapeRule ? Diamond : Circle;
  return <div className="precision-game">
    <PrecisionHUD round={round + 1} total={rules.rounds} combo={stats.combo} mistakes={stats.mistakes} limit={rules.mistakeLimit} progress={remaining} label={rules.level >= 2 ? '规则切换 · 抗干扰' : '信号辨识 · 抗干扰'} />
    <div className="precision-rule"><span>本轮指令</span><strong>仅点击绿色{shapeRule ? '菱形' : '圆形'}</strong><small>限时 {rules.reactionWindow} 毫秒 · {rules.perfectWindow} 毫秒内精准命中</small></div>
    <button type="button" className={`precision-signal ${phase}`} aria-label={phase === 'go' ? '点击目标信号' : phase === 'decoy' ? '干扰信号，请勿点击' : phase === 'feedback' ? feedback : '等待信号，请勿点击'}
      data-phase={phase} onPointerDown={e => { if (!e.isPrimary || e.button !== 0) return; e.preventDefault(); e.currentTarget.focus(); tap(); }}
      onKeyDown={e => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); tap(); } }}
      onClick={e => { if (e.detail === 0) tap(); }}>
      <span className="precision-orbit" aria-hidden="true" />
      {phase === 'go' ? <Shape size={70} strokeWidth={2.3} /> : phase === 'decoy' ? (shapeRule ? <Circle size={70} /> : <Diamond size={70} />) : phase === 'feedback' ? (isGood ? <Check size={56} /> : <X size={56} />) : <Hand size={44} strokeWidth={1.3} />}
      <strong>{phase === 'go' ? '现在点击' : phase === 'decoy' ? '不要点击' : phase === 'feedback' ? feedback : '保持专注'}</strong>
      <span>{phase === 'wait' ? '等待目标信号出现' : phase === 'feedback' && trials.at(-1)?.responseMs != null ? `${trials.at(-1)!.responseMs} 毫秒` : phase === 'decoy' ? '这是干扰信号' : '轻触或按空格键'}</span>
    </button>
    <TrialStrip trials={trials} total={rules.rounds} />
    <PrecisionStats stats={[{ label: '本局评分', value: stats.score }, { label: '平均响应', value: stats.responseMs ? `${stats.responseMs} ms` : '—' }, { label: '精准命中', value: stats.perfectCount }]} />
    <p className="precision-note">不靠声音提示抢拍。低于 100 毫秒记为提前输入；未作答也会判定超时。</p>
  </div>;
}
